import { Link } from "@tanstack/react-router";
import type { SubmissionView } from "@/app/reviewQueries";
import { cellWhere, fmtWhen, STATUS_LABEL, STATUS_TONE, taskOf } from "./model";

/** Карточка вердикта глазами студента: статус, сводка ментора, перечень правок
 *  из комментариев (ссылки в ячейки S5), «исправить и отправить vN+1». */
export function StudentVerdict({ view, latest }: { view: SubmissionView; latest: boolean }) {
  const tone = STATUS_TONE[view.status];
  const fg = `var(--${tone})`;
  const soft = `var(--${tone}soft)`;
  return (
    <section data-testid="verdict" data-status={view.status} className="overflow-hidden rounded-md bg-card" style={{ border: `1px solid ${fg}` }}>
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: soft }}>
        <span className="font-mono text-[11px] uppercase tracking-[.06em]" style={{ color: fg }}>
          вердикт
        </span>
        <span className="flex items-center gap-1.5 font-mono text-[12px] font-semibold" style={{ color: fg }}>
          <span aria-hidden className="size-1.5 rounded-full bg-current" />
          {STATUS_LABEL[view.status]}
        </span>
      </div>
      <div className="flex flex-col gap-3 p-4">
        {view.status === "on_review" ? (
          <>
            <p className="m-0 text-[13px] leading-[1.55] text-ink2 text-pretty">
              Ментор смотрит процедуру, а не только автопроверки: разбиение данных, утечки, воспроизводимость. Обычно отвечает в течение 24 ч — комментарии появятся у ячеек, перечень правок соберётся из них.
            </p>
            <p className="m-0 font-mono text-[11px] text-ink2">отправлено {fmtWhen(view.submitted_at)}</p>
          </>
        ) : (
          <>
            <div className="font-mono text-[11px] text-ink2">
              {view.reviewer?.name ?? "ментор"}
              {view.verdict_at ? ` · ${fmtWhen(view.verdict_at)}` : ""}
            </div>
            {view.summary && <p className="m-0 text-[13px] leading-[1.55] text-ink text-pretty">{view.summary}</p>}
          </>
        )}
        {view.status === "changes_requested" && (
          <>
            {view.todos.length > 0 ? (
              <ol className="m-0 flex list-none flex-col gap-2 p-0">
                {view.todos.map((t, i) => {
                  const n = taskOf(t);
                  return (
                    <li key={`${t.cell_id}-${i}`} data-testid="todo" className="grid grid-cols-[18px_minmax(0,1fr)] gap-2">
                      <span className="font-mono text-[11.5px]" style={{ color: "var(--err)" }}>
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] leading-[1.5] text-ink text-pretty">{t.text}</span>
                        <Link to="/notebook/$assignmentId" params={{ assignmentId: view.assignment_id }} search={n ? { task: n } : {}} className="font-mono text-[10.5px] text-ink2 underline-offset-2 hover:text-acc hover:underline" title={t.cell_id}>
                          {cellWhere(t)} →
                        </Link>
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Перечень правок пуст — смотри комментарии ментора у ячеек.</p>
            )}
            {latest ? (
              <Link
                to="/notebook/$assignmentId"
                params={{ assignmentId: view.assignment_id }}
                className="inline-flex w-full justify-center rounded-sm border border-acc bg-acc px-4 py-[11px] font-mono text-[13px] font-medium leading-none text-accink no-underline hover:brightness-[1.08]"
                data-testid="fix-and-resubmit"
              >
                исправить и отправить v{view.iteration + 1}
              </Link>
            ) : (
              <p className="m-0 font-mono text-[11px] text-ink2">Это не последняя версия — исправления уходят из текущей.</p>
            )}
          </>
        )}
        {view.status === "accepted" && (
          <>
            <p className="m-0 text-[13px] leading-[1.55] text-ink2 text-pretty">Гейт шага пересчитан: критерий «домашка принята» выполнен. Треды остаются здесь — к ним можно вернуться.</p>
            <Link to="/step/$id" params={{ id: view.step_id }} className="font-mono text-[12px] text-acc underline-offset-2 hover:underline">
              к шагу →
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
